import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MODULES, dataTypesForModule } from '../catalog';
import { getOffer } from '../fixtures/sourceCatalog';
import { IntegrationDemo } from '../demoStore';
import { buildPointViews, summarisePoints } from '../selectors';
import { DataTypeKey } from '../types';
import EnrichmentPanel from './EnrichmentPanel';
import ImportImpactPanel from './ImportImpactPanel';
import IntegrationPointRow from './IntegrationPointRow';
import SourceChangeDialog from './SourceChangeDialog';
import { SourceChoice } from './SourceSelect';

interface IntegrationPointsTabProps {
  demo: IntegrationDemo;
  actorName: string;
  onOpenMapping: (dataType: DataTypeKey) => void;
  onOpenPreview: (dataType: DataTypeKey) => void;
}

interface PendingChange {
  dataType: DataTypeKey;
  choice: SourceChoice;
}

const IntegrationPointsTab: React.FC<IntegrationPointsTabProps> = ({
  demo, actorName, onOpenMapping, onOpenPreview,
}) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingChange | null>(null);

  const views = useMemo(
    () => buildPointViews(demo.state.points, demo.state.sources),
    [demo.state.points, demo.state.sources],
  );
  const summary = useMemo(() => summarisePoints(views), [views]);
  const hasExternalSource = demo.state.sources.length > 0;

  const pendingChoice = pending?.choice;
  const pendingSourceName = pendingChoice?.kind === 'external'
    ? demo.state.sources.find((s) => s.id === pendingChoice.sourceId)?.displayName
    : undefined;

  // Mapping only resets when moving to an external system, which is the only case that brings a
  // different field vocabulary.
  const resetsMapping = (() => {
    if (!pending) return false;
    const { choice } = pending;
    if (choice.kind !== 'external') return false;
    const source = demo.state.sources.find((s) => s.id === choice.sourceId);
    return Boolean(source && getOffer(source.system, pending.dataType));
  })();

  const confirmChange = () => {
    if (!pending) return;
    const { dataType, choice } = pending;
    if (choice.kind === 'external') demo.setPointOwner(dataType, 'external', choice.sourceId, actorName);
    else demo.setPointOwner(dataType, choice.kind, null, actorName);
    setPending(null);
  };

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-base font-semibold text-foreground">{t('extInt.points.heading')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('extInt.points.subheading')}</p>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="points-read-only">
          {t('extInt.points.readOnly')}
        </p>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="points-summary">
          {t('extInt.points.summary', {
            mapped: summary.mapped,
            total: summary.total,
            notSet: summary.notSet,
            notTracked: summary.notTracked,
          })}
        </p>
      </section>

      {!hasExternalSource && (
        <p
          className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground"
          data-testid="points-empty"
        >
          {t('extInt.points.empty')}
        </p>
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="hidden grid-cols-[2fr_1.4fr_1fr_1fr] gap-3 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid">
          <span>{t('extInt.points.column.dataType')}</span>
          <span>{t('extInt.points.column.source')}</span>
          <span>{t('extInt.points.column.status')}</span>
          <span>{t('extInt.points.column.lastUpdated')}</span>
        </div>

        {MODULES.map((module) => (
          <section key={module.key} data-testid={`module-group-${module.key}`}>
            <div className="border-t border-border bg-muted/40 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(module.labelKey)}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`module-description-${module.key}`}>
                {t(module.descriptionKey)}
              </p>
            </div>
            {dataTypesForModule(module.key).map((def) => {
              const view = views.find((v) => v.point.dataType === def.key);
              if (!view) return null;
              return (
                <IntegrationPointRow
                  key={def.key}
                  view={view}
                  sources={demo.state.sources}
                  onChoose={(choice) => setPending({ dataType: def.key, choice })}
                  onOpenMapping={() => onOpenMapping(def.key)}
                  onOpenPreview={() => onOpenPreview(def.key)}
                />
              );
            })}
          </section>
        ))}
      </div>

      {/* Sits beside the enrichment panel as a sibling framed section, so the payoff is docked in
          the page's existing rhythm rather than floated into a gap between the table and it. */}
      <ImportImpactPanel points={demo.state.points} />

      <EnrichmentPanel demo={demo} actorName={actorName} />

      <SourceChangeDialog
        open={pending !== null}
        dataType={pending?.dataType ?? null}
        choice={pending?.choice ?? null}
        sourceName={pendingSourceName}
        resetsMapping={resetsMapping}
        onCancel={() => setPending(null)}
        onConfirm={confirmChange}
      />
    </div>
  );
};

export default IntegrationPointsTab;
